import { zodResolver } from "@hookform/resolvers/zod"
import {
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { type FieldPath, useFieldArray, useForm, useWatch } from "react-hook-form"

import { InventoryProductDescription, InventoryQuantityDifference } from "../../../components"
import { TRANSFER_VARIANCE_REASON_TO_LABEL, TransferVarianceReason } from "../constants"
import { useTransferLookups } from "../hooks/useTransferLookups"
import {
  type TransferReceiveFormValues,
  transferConfirmReceivedSchema,
  transferReceiveFormSchema,
} from "../schemas/receive"
import { filterQuantityVariancePallets, toTransferReceiveFormValues } from "../utils/receive"
import { getLotRowBorderClassName } from "../utils/transfer"

import { TransferQuantityVarianceDialog } from "./TransferQuantityVarianceDialog"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { ProductStatusCell } from "@/components/table"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import type { Transfer } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { Option } from "@/types/form-fields"
import { formatNumber } from "@/utils/number"

const VARIANCE_REASON_OPTIONS: Option<TransferVarianceReason>[] = Object.values(TransferVarianceReason).map(
  (value) => ({ value, label: TRANSFER_VARIANCE_REASON_TO_LABEL[value] })
)

interface TransferReceiveFormDialogProps {
  transfer: Transfer
  onClose: () => void
  onSubmit: () => void
}

export const TransferReceiveFormDialog: FC<TransferReceiveFormDialogProps> = ({ transfer, onClose, onSubmit }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const [quantityVariancePallets, setQuantityVariancePallets] = useState<TransferReceiveFormValues["pallets"]>([])

  const form = useForm<TransferReceiveFormValues>({
    resolver: zodResolver(transferReceiveFormSchema),
    defaultValues: toTransferReceiveFormValues(transfer),
    mode: "onChange",
  })

  const {
    control,
    setError,
    formState: { isDirty, isValid },
  } = form

  const { fields } = useFieldArray({ control, name: "pallets" })
  const pallets = useWatch({ control, name: "pallets" })

  const { productsByReferenceNbr, transferLotsById } = useTransferLookups(transfer)

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
      return
    }

    onClose()
  }

  const handleSave = form.handleSubmit(() => {
    // TODO(AK-6103): Call the save-received-quantities mutation (no AS400 sync; the transfer stays `InTransit`) and call onSubmit only on success.
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
    onSubmit()
  })

  const handleConfirmReceived = form.handleSubmit((formValues) => {
    const validation = transferConfirmReceivedSchema.safeParse(formValues)

    if (!validation.success) {
      validation.error.issues.forEach(({ path, message }) =>
        setError(path.join(".") as FieldPath<TransferReceiveFormValues>, { type: "custom", message })
      )
      return
    }

    const variancePallets = filterQuantityVariancePallets(formValues.pallets)

    if (variancePallets.length) {
      setQuantityVariancePallets(variancePallets)
      return
    }

    // TODO(AK-6103): Call the confirm-received mutation and call onSubmit only on success.
    // Akrochem-managed target warehouse: AEROS creates and confirms a work order, then the transfer moves to `Received`.
    // Any other target warehouse: the transfer moves straight to `Completed`.
    // On failure the transfer stays `InTransit` with a red sync error icon, and reopening the modal preloads the saved values (AC12, AC13).
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
    onSubmit()
  })

  const handleQuantityVarianceClose = () => setQuantityVariancePallets([])

  const handleAdjustAutomatically = () => {
    // TODO(AK-6878): Submit one upsert adjustment event per variance lot (negative for under-received, positive for over-received), then create and confirm the work order and show the receiving confirmed message (AC3, AC5).
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  const handleAdjustManually = () => {
    // TODO(AK-6878): Skip the adjustments, create and confirm the work order, and show the receiving confirmed message with the open-variance warning (AC6).
    notify({ type: NotificationType.Warning, message: "Not implemented yet" })
  }

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  if (quantityVariancePallets.length) {
    return (
      <TransferQuantityVarianceDialog
        transfer={transfer}
        pallets={quantityVariancePallets}
        onClose={handleQuantityVarianceClose}
        onAdjustAutomatically={handleAdjustAutomatically}
        onAdjustManually={handleAdjustManually}
      />
    )
  }

  return (
    <Dialog open onClose={handleCancel} maxWidth={false} fullWidth data-testid='transfer.receive-form-dialog'>
      <DialogHeader title='Confirm Received' onClose={handleCancel} />
      <DialogContent className='p-10'>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Product ID</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Reference #</TableCell>
              <TableCell>LPN</TableCell>
              <TableCell>Lot #</TableCell>
              <TableCell>Batch #</TableCell>
              <TableCell>Qty Expected</TableCell>
              <TableCell>
                Qty Received<span className='text-secondary-600'>*</span>
              </TableCell>
              <TableCell>Difference</TableCell>
              <TableCell>Variance Reason</TableCell>
              <TableCell>Variance Comments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.flatMap((pallet, palletIndex) => {
              const product = productsByReferenceNbr.get(pallet.referenceNbr)!
              const uomName = product.packageUsWeightUomDetails?.name ?? ""

              return pallet.lots.map((lot, lotIndex) => {
                const isFirstLot = lotIndex === 0
                const isLastLot = lotIndex === pallet.lots.length - 1
                const transferLot = transferLotsById.get(lot.transferLotId)!

                return (
                  <TableRow
                    key={lot.transferLotId}
                    className={getLotRowBorderClassName(isLastLot)}
                    data-testid={`transfer.receive-form-dialog.row-${lot.transferLotId}`}
                  >
                    {isFirstLot ? (
                      <>
                        <TableCell className='py-2'>
                          <ProductStatusCell product={product} />
                        </TableCell>
                        <TableCell className='py-2'>
                          <InventoryProductDescription product={product} />
                        </TableCell>
                        <TableCell>{pallet.referenceNbr}</TableCell>
                        <TableCell className='min-w-40'>
                          <FormTextField
                            control={control}
                            name={`pallets.${palletIndex}.lpn`}
                            data-testid={`transfer.receive-form-dialog.lpn-field-${pallet.referenceNbr}`}
                          />
                        </TableCell>
                      </>
                    ) : (
                      <TableCell colSpan={4} />
                    )}
                    <TableCell>{transferLot.rawInventory?.lotNbr}</TableCell>
                    <TableCell>{transferLot.rawInventory?.batchNbr}</TableCell>
                    <TableCell className='whitespace-nowrap'>
                      {formatNumber(lot.qtyExpected)} {uomName}
                    </TableCell>
                    <TableCell className='min-w-40'>
                      <FormNumericField
                        control={control}
                        name={`pallets.${palletIndex}.lots.${lotIndex}.qtyReceived`}
                        allowNegative={false}
                        decimalScale={2}
                        slotProps={{
                          input: { endAdornment: <InputAdornment position='end'>{uomName}</InputAdornment> },
                        }}
                        data-testid={`transfer.receive-form-dialog.qty-received-field-${lot.transferLotId}`}
                      />
                    </TableCell>
                    <TableCell className='whitespace-nowrap'>
                      <InventoryQuantityDifference
                        quantity={pallets[palletIndex].lots[lotIndex].qtyReceived}
                        originalQuantity={lot.qtyExpected}
                        uomName={uomName}
                        showZero
                      />
                    </TableCell>
                    <TableCell className='min-w-52'>
                      <FormSelect
                        control={control}
                        name={`pallets.${palletIndex}.lots.${lotIndex}.varianceReason`}
                        options={VARIANCE_REASON_OPTIONS}
                        data-testid={`transfer.receive-form-dialog.variance-reason-field-${lot.transferLotId}`}
                      />
                    </TableCell>
                    <TableCell className='min-w-52'>
                      <FormTextField
                        control={control}
                        name={`pallets.${palletIndex}.lots.${lotIndex}.varianceComments`}
                        data-testid={`transfer.receive-form-dialog.variance-comments-field-${lot.transferLotId}`}
                      />
                    </TableCell>
                  </TableRow>
                )
              })
            })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions className='px-10 pb-10'>
        <Button appearance='outlined' onClick={handleCancel} data-testid='transfer.receive-form-dialog.cancel-button'>
          Cancel
        </Button>
        <div className='flex flex-1 justify-end gap-x-4'>
          <Button
            appearance='outlined'
            disabled={!isDirty || !isValid}
            onClick={handleSave}
            data-testid='transfer.receive-form-dialog.save-later-button'
          >
            Save Changes and Finish Later
          </Button>
          <Button
            appearance='contained'
            disabled={!isValid}
            onClick={handleConfirmReceived}
            data-testid='transfer.receive-form-dialog.confirm-button'
          >
            Save & Confirm Received
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  )
}
